/**
 * NotesSection — the reusable notes CRUD block (add / edit / list), scoped
 * to any subject via the generic (subjectType, subjectId) connection, or to
 * a bare customer_id when neither is set.
 *
 * Generalised from the former JobNotesSection (job-scoped only) so the same
 * add/edit/list behaviour serves job notes and per-contact notes
 * (ContactDetailPage) without hand-maintained copies. NotesPage uses the
 * add-only NoteComposer directly instead, since it already owns its own
 * search/sort/paginated list.
 *
 * `jobPickerCustomerId`, forwarded to NoteComposer, adds the optional
 * "attach to a job" step described there.
 */

import { useCallback, useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type * as API from "@shared-core/data/api-types";
import {
  formatShortDateOrDash,
  formatTimeOrDash,
} from "@shared-core/domain/formatters";
import { Button } from "../../primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../primitives/card";
import { useAuth } from "../../../auth/hooks";
import { NoteComposer } from "./NoteComposer";

export interface NotesSectionProps {
  /** Section heading. Defaults to "Notes". */
  title?: string;
  /** The generic connection this section reads/writes. Both or neither. */
  subjectType?: string;
  subjectId?: string;
  /** Display snapshot of the subject, stamped on new notes. */
  subjectLabel?: string;
  /** Scopes notes to a customer (the tenancy/portfolio-visibility axis). */
  customerId?: string;
  /** Enables the "attach to a job" step on the add-note form — see
   *  NoteComposer's docstring. */
  jobPickerCustomerId?: string;
}

export function NotesSection({
  title = "Notes",
  subjectType,
  subjectId,
  subjectLabel,
  customerId,
  jobPickerCustomerId,
}: NotesSectionProps) {
  const dataAdapter = useDataAdapter();
  const { user } = useAuth();
  const [notes, setNotes] = useState<API.Note[]>([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editNoteText, setEditNoteText] = useState("");

  const loadNotes = useCallback(async () => {
    setNotesLoading(true);
    try {
      const result = await dataAdapter.listNotes({
        subjectType,
        subjectId,
        customerId: subjectType ? undefined : customerId,
        sort: "-created_at",
      });
      setNotes(result.items);
    } catch (err) {
      console.error("notes_section.list_failed", {
        status: err instanceof Error ? err.message : "unknown",
      });
    } finally {
      setNotesLoading(false);
    }
  }, [dataAdapter, subjectType, subjectId, customerId]);

  useEffect(() => {
    void loadNotes();
  }, [loadNotes]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{title}</CardTitle>
          <NoteComposer
            subjectType={subjectType}
            subjectId={subjectId}
            subjectLabel={subjectLabel}
            customerId={customerId}
            jobPickerCustomerId={jobPickerCustomerId}
            onCreated={loadNotes}
          />
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-3">
        {notesLoading ? (
          <p className="text-sm text-muted-foreground">Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No notes yet.</p>
        ) : (
          <ul className="space-y-2">
            {notes.map((note) => (
              <li key={note.id}>
                {editingNoteId === note.id ? (
                  <div className="space-y-2">
                    <textarea
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 min-h-[80px] resize-y"
                      value={editNoteText}
                      onChange={(e) => setEditNoteText(e.target.value)}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        disabled={!editNoteText.trim() || submitting}
                        onClick={async () => {
                          if (!editNoteText.trim()) return;
                          setSubmitting(true);
                          try {
                            await dataAdapter.updateNote(note.id, {
                              note_text: editNoteText.trim(),
                            });
                            setEditingNoteId(null);
                            await loadNotes();
                          } catch (err) {
                            console.error("notes_section.update_failed", {
                              status:
                                err instanceof Error ? err.message : "unknown",
                            });
                          } finally {
                            setSubmitting(false);
                          }
                        }}
                      >
                        {submitting ? "Saving…" : "Save"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditingNoteId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between gap-2 p-3 rounded-md border border-border/60 bg-muted/30 text-sm">
                    <div className="flex-1 min-w-0">
                      {note.note_title && (
                        <p className="text-xs font-medium text-muted-foreground mb-0.5">
                          {note.note_title}
                        </p>
                      )}
                      <p className="leading-snug whitespace-pre-wrap">
                        {note.note_text}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {formatShortDateOrDash(note.created_at)},{" "}
                        {formatTimeOrDash(note.created_at)}
                        {note.tharstern_note_id != null &&
                          " · sent to Tharstern"}
                      </p>
                    </div>
                    {note.user_id === user?.id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="shrink-0 h-6 w-6 p-0"
                        aria-label="Edit note"
                        onClick={() => {
                          setEditingNoteId(note.id);
                          setEditNoteText(note.note_text);
                        }}
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
